import axios, { AxiosResponse } from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
} from '../utils/compass'
import { datetimeXAxis } from '../utils/constants'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const POWER_CHARTS_PATH = '/api/charts/power'

const commonAbsoluteSeriesConfig = {
  marker: { enabled: false },
  tooltip: { valueDecimals: 2 },
  type: 'spline',
  yAxis: 'default',
}

export const defaultYAxis = {
  id: 'default',
  opposite: false,
  title: { text: '' },
  labels: { format: '{value}' },
  startOnTick: false,
  endOnTick: false,
}

type CargaHorariaRegion = 'BR' | 'S' | 'SE' | 'N' | 'NE'

type Weekday =
  | 'All Days'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday'
  | 'Sunday'

type ImportacaoChartId =
  | 'importacao_total'
  | 'importacao_uruguai'
  | 'importacao_paraguai'
  | 'importacao_argentina'

type ExportacaoChartId =
  | 'exportacao_total'
  | 'exportacao_uruguai'
  | 'exportacao_paraguai'
  | 'exportacao_argentina'

export type ImportacaoExportacaoChartId = ImportacaoChartId | ExportacaoChartId

export namespace Power {
  export namespace DadosDiarios {
    const CONTEXT_ID = 'equity:power:dados_diarios'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'br' | 's' | 'se' | 'n' | 'ne'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [true],
      })(config, data)
  }

  export namespace CargaHoraria {
    const CONTEXT_ID = 'equity:power:carga_horaria'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 's' | 'se' | 'n' | 'ne'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartProps(config, data),
    }

    const getChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        convertToPercentages: [true],
      })(config, data)
  }

  export namespace CargaHorariaAno {
    const CONTEXT_ID = 'equity:power:carga_horaria_ano'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      region: CargaHorariaRegion
      weekday: Weekday
      context: typeof CONTEXT_ID
    }

    interface ChartData extends CompassAPIDataBase {
      title: string
      data: {
        name: string
        values: {
          x_value: number
          y_value: number
        }[]
      }[]
    }

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: POWER_CHARTS_PATH + '/carga-por-hora/ano',
        method: 'POST',
        data: { region: config.region, weekday: config.weekday },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        ...commonAbsoluteSeriesConfig,
        color: chartPalette[data.data.length - 1 - idx],
        data: s.values.map(({ x_value, y_value }) => [x_value, y_value]),
        id: `series-${idx}`,
        name: s.name,
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const xAxis = [
        {
          type: 'category' as const,
          categories: [...new Array(24)].map(
            (_, idx) => idx.toString().padStart(2, '0') + ':00'
          ),
          labels: { rotation: -45 },
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            legend: { maxHeight: 80 },
            tooltip: { shared: false, split: true },
            xAxis: xAxis,
            yAxis: [defaultYAxis],
            series,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.region}:${config.weekday}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace CargaHorariaDia {
    const CONTEXT_ID = 'equity:power:carga_horaria_dia'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      region: CargaHorariaRegion
      weekday: Weekday
      daysMA: number
      isPercantage: boolean
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const CARGA_HORARIA_DIA_TIMEOUT = 30000

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        timeout: CARGA_HORARIA_DIA_TIMEOUT,
        url: POWER_CHARTS_PATH + '/carga-por-hora/dia',
        method: 'POST',
        data: {
          region: config.region,
          percentage: config.isPercantage,
          weekday: config.weekday,
          days_moving_avg: config.daysMA,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: config.isPercantage,
        }),
        id: `series-${idx}`,
        name: s.name.toString().padStart(2, '0') + ':00',
        marker: { enabled: false },
        tooltip: {
          valueDecimals: 2,
          valueSuffix: config.isPercantage ? '%' : '',
          xDateFormat: '%A, %b %e, %Y',
        },
        type: 'spline',
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: `{value}${config.isPercantage ? '%' : ''}` },
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            legend: { maxHeight: 80 },
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            yAxis,
            series,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ImportacaoExportacao {
    const CONTEXT_ID = 'equity:power:importacao_exportacao'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      chart: ImportacaoExportacaoChartId
      series: string[]
      oneSeriesPerYear: boolean
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const IMPORTACAO_EXPORTACAO_PATHS: Record<
      ImportacaoExportacaoChartId,
      string
    > = {
      importacao_total: '/importacao-exportacao/importacao-total',
      importacao_uruguai: '/importacao-exportacao/importacao-uruguai',
      importacao_paraguai: '/importacao-exportacao/importacao-paraguai',
      importacao_argentina: '/importacao-exportacao/importacao-argentina',
      exportacao_total: '/importacao-exportacao/exportacao-total',
      exportacao_uruguai: '/importacao-exportacao/exportacao-uruguai',
      exportacao_paraguai: '/importacao-exportacao/exportacao-paraguai',
      exportacao_argentina: '/importacao-exportacao/exportacao-argentina',
    }

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: POWER_CHARTS_PATH + IMPORTACAO_EXPORTACAO_PATHS[config.chart],
        method: 'POST',
        data: {
          series: config.series,
          one_series_per_year: config.oneSeriesPerYear,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          legend: { maxHeight: 80 },
          tooltip: { shared: false, split: true },
          chartTitle: data.title,
          xAxis: datetimeXAxis[0],
        },
        { name: data.data.map((s) => capitalizeString(s.name)) }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace IntercambioCarga {
    const CONTEXT_ID = 'equity:power:intercambio_carga'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type IntercambioId =
      | 'intercambio_br'
      | 'intercambio_se'
      | 'intercambio_s'
      | 'intercambio_ne'
      | 'intercambio_n'

    type CargaId = 'carga_br' | 'carga_se' | 'carga_s' | 'carga_ne' | 'carga_n'

    type IntercambioCargaChartId = IntercambioId | CargaId

    export interface ChartConfig {
      title?: string
      chart: IntercambioCargaChartId
      oneSeriesPerYear: boolean
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const INTERCAMBIO_CARGA_CHART_PATHS: Record<
      IntercambioCargaChartId,
      string
    > = {
      intercambio_br: '/intercambio-carga/intercambio-br',
      intercambio_se: '/intercambio-carga/intercambio-se',
      intercambio_s: '/intercambio-carga/intercambio-s',
      intercambio_ne: '/intercambio-carga/intercambio-ne',
      intercambio_n: '/intercambio-carga/intercambio-n',
      carga_br: '/intercambio-carga/carga-br',
      carga_se: '/intercambio-carga/carga-se',
      carga_s: '/intercambio-carga/carga-s',
      carga_ne: '/intercambio-carga/carga-ne',
      carga_n: '/intercambio-carga/carga-n',
    }

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: POWER_CHARTS_PATH + INTERCAMBIO_CARGA_CHART_PATHS[config.chart],
        method: 'POST',
        data: { one_series_per_year: config.oneSeriesPerYear },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        legend: { maxHeight: 80 },
        tooltip: { shared: false, split: true },
        chartTitle: data.title,
        xAxis: datetimeXAxis[0],
      })(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${config.oneSeriesPerYear}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Ons {
    const CONTEXT_ID = 'equity:power:ons'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type EnergiaNaturalAfluenteChartId =
      | 'energia_natural_afluente_sudeste_centro_oeste'
      | 'energia_natural_afluente_norte'
      | 'energia_natural_afluente_sul'
      | 'energia_natural_afluente_nordeste'

    type VariacaoEnergiaArmazenadaChartId =
      | 'variacao_energia_armazenada_sudeste_centro_oeste'
      | 'variacao_energia_armazenada_norte'
      | 'variacao_energia_armazenada_sul'
      | 'variacao_energia_armazenada_nordeste'

    export type OnsChartId =
      | EnergiaNaturalAfluenteChartId
      | VariacaoEnergiaArmazenadaChartId

    export interface ChartConfig {
      title?: string
      chart: OnsChartId
      series: string[]
      oneSeriesPerYear: boolean
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const ONS_CHART_PATHS: Record<OnsChartId, string> = {
      energia_natural_afluente_sudeste_centro_oeste:
        '/energia-natural-afluente/sudeste-centro-oeste-refactored',
      energia_natural_afluente_norte:
        '/energia-natural-afluente/norte-refactored',
      energia_natural_afluente_sul: '/energia-natural-afluente/sul-refactored',
      energia_natural_afluente_nordeste:
        '/energia-natural-afluente/nordeste-refactored',
      variacao_energia_armazenada_sudeste_centro_oeste:
        '/variacao-energia-armazenada/sudeste-centro-oeste-refactored',
      variacao_energia_armazenada_norte:
        '/variacao-energia-armazenada/norte-refactored',
      variacao_energia_armazenada_sul:
        '/variacao-energia-armazenada/sul-refactored',
      variacao_energia_armazenada_nordeste:
        '/variacao-energia-armazenada/nordeste-refactored',
    }

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: POWER_CHARTS_PATH + ONS_CHART_PATHS[config.chart],
        method: 'POST',
        data: {
          series: config.series,
          one_series_per_year: config.oneSeriesPerYear,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const isPercentSeries = (id: string) =>
      ['Perc', '%'].some((term) => id.includes(term))

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          legend: { maxHeight: 80 },
          tooltip: { shared: false, split: true },
          chartTitle: data.title,
          xAxis: datetimeXAxis[0],
          yAxis: [
            {
              id: 'percentual',
              opposite: false,
              labelSuffix: '%',
            },
            {
              id: 'absolute',
              opposite: true,
            },
          ],
        },
        {
          yAxis: data.data.map((s) =>
            isPercentSeries(s.name) ? 'percentual' : 'absolute'
          ),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ProducaoSubsistema {
    const CONTEXT_ID = 'equity:power:producao_subsistema'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    type ProducaoSubsistemaRegion = 'BR' | 'S' | 'SE' | 'N' | 'NE' | 'ITAIPU'
    export interface ChartConfig {
      title?: string
      region: ProducaoSubsistemaRegion
      percentage: boolean
      weekday: Weekday
      daysMovingAvg: number
      series: string[]
      isYearVsYear: boolean
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url:
          POWER_CHARTS_PATH +
          '/producao-por-tipo/producao-subsistema-refactored',
        method: 'POST',
        data: {
          region: config.region,
          percentage: config.percentage,
          weekday: config.weekday,
          days_moving_avg: config.daysMovingAvg,
          series: config.series,
          one_series_per_year: config.isYearVsYear,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data.map((s, idx) => ({
        ...(data.data.length === 1 ? { showInLegend: false } : {}),
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: config.percentage,
        }),
        id: `series-${idx}`,
        name: capitalizeString(s.name),
        marker: { enabled: false },
        stacking:
          config.percentage && !config.isYearVsYear ? 'normal' : undefined,
        tooltip: {
          valueDecimals: 2,
          valueSuffix: config.percentage ? '%' : '',
        },
        type: config.percentage && !config.isYearVsYear ? 'area' : 'spline',
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        {
          id: 'default',
          opposite: false,
          title: { text: '' },
          labels: { format: `{value}${config.percentage ? '%' : ''}` },
          min: config.percentage ? 0 : undefined,
          max: config.percentage ? 100 : undefined,
          startOnTick: false,
          endOnTick: false,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            colors: chartPalette,
            chart: { zoomType: 'x' },
            series: series as any,
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace ProducaoUsina {
    const CONTEXT_ID = 'equity:power:producao_usina'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      usinas: string[]
      prodType: 'hidraulica' | 'eolica' | 'solar' | 'termica'
      percentage: boolean
      weekday: Weekday
      daysMA: number
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: POWER_CHARTS_PATH + '/producao-por-tipo/producao-usina',
        method: 'POST',
        data: {
          usinas: config.usinas,
          prod_type: config.prodType,
          percentage: config.percentage,
          weekday: config.weekday,
          days_moving_avg: config.daysMA,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(config.percentage),
        },
        {
          name: data.data.map((s) =>
            capitalizeString(s.name.split('_').join(' '))
          ),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace Geracao {
    const CONTEXT_ID = 'equity:power:geracao'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      chart:
        | 'usina-mensal-producao'
        | 'usina-diario-producao'
        | 'usina-mensal-fator-capacidade'
        | 'usina-mensal-producao-media-horaria'
      title?: string
      type: 'solar' | 'eolica'
      usinas: string[]
      percentage?: boolean
      weekday?: Weekday
      daysMA?: number
      context: typeof CONTEXT_ID
    }

    type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) => {
      const response = (await axios({
        url: `${POWER_CHARTS_PATH}/geracao-${config.type}/${config.chart}`,
        method: 'POST',
        data: {
          usinas: config.usinas,
          percentage: config.percentage,
          weekday: config.weekday,
          days_moving_avg: config.daysMA,
        },
      })) as AxiosResponse<ChartData>

      return response.data
    }

    const getChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          legend:
            config.chart === 'usina-mensal-producao-media-horaria'
              ? { maxHeight: 80 }
              : undefined,
          enableMarker: false,
          categorical: config.chart === 'usina-mensal-producao-media-horaria',
          convertToPercentages: Array(data.data.length).fill(config.percentage),
          useRegularChart:
            config.chart === 'usina-mensal-producao-media-horaria',
          xAxis:
            config.chart === 'usina-mensal-producao-media-horaria'
              ? {
                  type: 'category' as const,
                  categories: [...new Array(24)].map(
                    (_, idx) => idx.toString().padStart(2, '0') + ':00'
                  ),
                  labels: { rotation: -45 },
                }
              : undefined,
          yAxis:
            config.chart === 'usina-mensal-producao-media-horaria'
              ? [
                  {
                    id: 'default',
                    opposite: false,
                    title: { text: '' },
                  },
                ]
              : undefined,
        },
        {
          name: data.data.map((s) =>
            config.chart === 'usina-diario-producao'
              ? s.name.toString().padStart(2, '0') + ':00'
              : s.name
          ),
          type: Array(data.data.length).fill('spline'),
        }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${hash(config, { unorderedArrays: true })}`,
      getChartData,
      getChartProps,
    }
  }
}
